import React, { useCallback, useState } from 'react'
import {
  Dispatch,
  recordingCountdownSeconds,
  WebRecorder2State,
  downloadRecording,
  Callbacks,
  submitRecording,
  handleUploadError,
  WebRecorder2Action,
} from '~/webRecorder2/webRecorder2'
import { UnsubmittedRecording } from '~/webRecorder2/useUnsubmittedRecordings'
import { formatDurationSeconds, secondsUntil } from '~/utils/time'
import { Analyzable, FrequencyGraph } from '~/webRecorder2/FrequencyGraph'
import { MeetingTitle, RecordingDateTime } from './shared'
import { IconButton } from '~/components/IconButton'
import { AudioInputSelector } from '~/webRecorder2/microphoneInput/react'
import {
  PlayingDisclosureView,
  StartRecordingIcon,
  TickUpdated,
  ContainerOverFrequencyVisualizer,
  PauseButton,
  DownloadButton,
  UploadProgress,
} from '~/webRecorder2/shared'
import { Loading } from '~/components/Loading'
import * as Recorder from '~/webRecorder2/recorder'
import * as RecordingStorage from '~/webRecorder2/recordingStorage'
import { Button } from '~/components/Button'
import { Modal } from '~/components/Modal'
import { TrashIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useWebRecorderPubSubSubscribe } from '~/webRecorder2/useWebRecorderPubSubSubscribe'
import { useIsOnline } from '~/utils/connectivity/useIsOnline'

export function UnsubmittedRecordingView({
  recording,
  recordingState,
  canDownload,
  showDownloadButtonBeforeRecording,
  recordingDispatch,
  trackAnalytics,
  uploadRecording,
  hideRecording,
  yieldRecordingToMeetingDetail,
}: {
  recording: UnsubmittedRecording
  recordingState?: WebRecorder2State
  canDownload: boolean
  showDownloadButtonBeforeRecording: boolean
  recordingDispatch?: Dispatch
  trackAnalytics: Callbacks['trackAnalytics']
  uploadRecording: Callbacks['upload']
  hideRecording: () => void
  yieldRecordingToMeetingDetail: () => void
}) {
  const subjectId = 'meetingId' in recording ? recording.meetingId : recording.intRefId

  const isActiveRecording =
    recordingDispatch && recordingState && 'storage' in recordingState && recordingState.storage.subjectId === subjectId

  return (
    <div>
      {isActiveRecording ? (
        <SidebarActiveRecordingView
          recording={recording}
          state={recordingState}
          canDownload={canDownload}
          showDownloadButtonBeforeRecording={showDownloadButtonBeforeRecording}
          dispatch={recordingDispatch}
          yieldRecordingToMeetingDetail={yieldRecordingToMeetingDetail}
          trackAnalytics={trackAnalytics}
        />
      ) : (
        <InactiveRecordingView
          recording={recording}
          canDownload={canDownload}
          startRecording={() =>
            recordingDispatch?.({ action: 'set-subject-id', subjectId, startRecording: 'immediately' })
          }
          uploadRecording={uploadRecording}
          trackAnalytics={trackAnalytics}
          hideRecording={hideRecording}
        />
      )}
    </div>
  )
}

function InactiveRecordingView({
  recording,
  canDownload,
  startRecording,
  uploadRecording,
  trackAnalytics,
  hideRecording,
}: {
  recording: UnsubmittedRecording
  canDownload: boolean
  startRecording: () => void
  uploadRecording: Callbacks['upload']
  trackAnalytics: Callbacks['trackAnalytics']
  hideRecording: () => void
}) {
  const subjectId = 'meetingId' in recording ? recording.meetingId : recording.intRefId

  const [uploadPercent, setUploadPercent] = useState<number | undefined>(undefined)

  const download = useCallback(async () => {
    downloadDirectFromIndexedDb({ subjectId, name: recording.name, trackAnalytics })
  }, [subjectId, trackAnalytics])

  const submit = useCallback(async () => {
    try {
      const storage = await RecordingStorage.openExistingStorage(subjectId, 'notetaker')

      if (storage) {
        const mimeType = RecordingStorage.getMimeType(storage)

        /* eslint-disable-next-line no-console */
        await submitRecording(storage, mimeType, uploadRecording, trackAnalytics, console.log)

        setUploadPercent(100)
      }

      setTimeout(hideRecording, 5_000)
    } catch (error) {
      /* eslint-disable-next-line no-console */
      handleUploadError(error, console.error, trackAnalytics, () => {})
    }
  }, [subjectId, uploadRecording, trackAnalytics])

  const destroy = useCallback(async () => {
    try {
      const storage = await RecordingStorage.openExistingStorage(subjectId, 'notetaker')

      if (storage) {
        await RecordingStorage.clear(storage)
      }

      hideRecording()
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Error deleting recording', error)
    }
  }, [subjectId])

  useWebRecorderPubSubSubscribe<WebRecorder2Action & { subjectId?: string }>(
    'apply-action',
    (action) => {
      const isUploadProgressForCurrentSubject =
        action.action === 'update-upload-progress' && 'subjectId' in action && action.subjectId === subjectId

      if (isUploadProgressForCurrentSubject) {
        setUploadPercent(action.progressPercent)
      }
    },
    [subjectId, setUploadPercent]
  )

  const isOnline = useIsOnline()

  return (
    <div>
      <div className="relative mb-4 mt-2">
        <RecordButtonAndFrequencyGraph startRecording={startRecording} />
      </div>

      <MeetingTitle title={recording.name} href={url(recording)} />

      <div className="flex items-center gap-x-2 justify-between">
        <RecordingDateTime startedAtUnixMs={recording.startedAtUnixMs} />
        <div className="text-secondary justify-self-end tabular-nums">
          {formatDurationSeconds(recording.durationMs / 1_000)}
        </div>
      </div>

      <PausedBottomControls
        // Don't allow submitting a recording from the past that shows 0 seconds
        onSubmit={recording.durationMs >= 500 ? submit : undefined}
        onDelete={destroy}
        onDownload={canDownload ? download : undefined}
        uploadPercent={uploadPercent}
        isOnline={isOnline}
      />
    </div>
  )
}

function RecordButtonAndFrequencyGraph({
  startRecording,
  countdown,
}: {
  startRecording?: () => void
  countdown?: React.ReactElement | number
}) {
  return (
    <>
      <SilentFrequencyGraph />
      <ContainerOverFrequencyVisualizer>
        <IconButton
          icon={<StartRecordingIcon size="lg" />}
          screenReaderText="Capture meeting now"
          tooltip="Capture meeting now"
          variant="outline-circle"
          size="lg"
          onClick={startRecording}
          disabled={!startRecording}
        />
      </ContainerOverFrequencyVisualizer>
      {countdown !== undefined && <ContainerOverFrequencyVisualizer>{countdown}</ContainerOverFrequencyVisualizer>}
    </>
  )
}

const frequencyVisualizerHeight = 56

function SilentFrequencyGraph() {
  const fakeAnalyser: Analyzable = {
    frequencyBinCount: 96,
    getByteFrequencyData: (arr) => {
      arr.fill(0)
    },
  }

  const bgTertiary = '#e5e7eb'
  return <FrequencyGraph analyser={fakeAnalyser} height={frequencyVisualizerHeight} barColor={bgTertiary} />
}

export function SidebarActiveRecordingView({
  recording,
  state,
  canDownload,
  showDownloadButtonBeforeRecording,
  dispatch,
  yieldRecordingToMeetingDetail,
  trackAnalytics,
}: {
  recording: UnsubmittedRecording
  state: WebRecorder2State
  canDownload: boolean
  showDownloadButtonBeforeRecording: boolean
  dispatch: Dispatch
  yieldRecordingToMeetingDetail: () => void
  trackAnalytics: Callbacks['trackAnalytics']
}) {
  // TODO: This needs to come from the server
  const otherRemoteClientIsRecording = false

  const subjectId = 'meetingId' in recording ? recording.meetingId : recording.intRefId

  return (
    <div>
      {!['completed', 'uploading'].includes(state.mode) && (
        <div className="relative mb-4 mt-2">
          <div className="flex flex-row gap-2 items-center">
            {state.errorMessage ? (
              <div className="text-xs text-red-500 text-center pt-2">{state.errorMessage}</div>
            ) : state.otherLocalClientIsRecording && state.mode !== 'recording' ? (
              <Loading size="sm">You’re capturing a meeting in another tab&hellip;</Loading>
            ) : otherRemoteClientIsRecording && state.mode !== 'recording' ? (
              <Loading size="sm">Another browser is capturing this meeting&hellip;</Loading>
            ) : (
              <AudioVisualizerAndRecordingButton
                state={state}
                startNewRecording={() =>
                  dispatch({ action: 'set-subject-id', subjectId, startRecording: 'immediately' })
                }
                pauseRecording={() => {
                  dispatch({ action: 'pause-recording' })
                  yieldRecordingToMeetingDetail()
                }}
                resumeRecording={() => dispatch({ action: 'resume-recording' })}
              />
            )}
          </div>
        </div>
      )}

      <TitleAndMetadata recording={recording} recorder={'recorder' in state ? state.recorder : undefined} />

      <BottomControls
        state={state}
        dispatch={dispatch}
        canDownload={canDownload}
        showDownloadButtonBeforeRecording={showDownloadButtonBeforeRecording}
        onEmergencyDownload={() => downloadDirectFromIndexedDb({ subjectId, name: recording.name, trackAnalytics })}
      />
    </div>
  )
}

function TitleAndMetadata({
  recording,
  recorder,
}: {
  recording: UnsubmittedRecording
  recorder?: Recorder.ActiveState | Recorder.PausedState
}) {
  const currentDuration = useCallback(
    (now: number) => {
      const durationSeconds = recorder ? Recorder.recordingDurationSeconds(recorder, now) : recording.durationMs / 1_000
      return (
        <div className="text-secondary justify-self-end tabular-nums">{formatDurationSeconds(durationSeconds)}</div>
      )
    },
    [recorder && recorder.prevDurationMs, recorder && 'startTimeMs' in recorder && recorder.startTimeMs]
  )

  return (
    <div>
      <MeetingTitle title={recording.name} href={url(recording)} />
      <div className="flex items-center gap-x-2 justify-between">
        <RecordingDateTime startedAtUnixMs={recording.startedAtUnixMs} />
        <TickUpdated currentValue={currentDuration} />
      </div>
    </div>
  )
}

function AudioVisualizerAndRecordingButton({
  state,
  startNewRecording,
  pauseRecording,
  resumeRecording,
}: {
  state: WebRecorder2State
  startNewRecording: () => void
  pauseRecording: () => void
  resumeRecording: () => void
}): React.ReactElement {
  switch (state.mode) {
    case 'initial':
      return <RecordButtonAndFrequencyGraph />

    case 'initialized': {
      return state.initializingStream ? (
        <RecordButtonAndFrequencyGraph />
      ) : (
        <RecordButtonAndFrequencyGraph startRecording={startNewRecording} />
      )
    }

    case 'playing-disclosure':
      return <PlayingDisclosureView />

    case 'recording': {
      return (
        <>
          <FrequencyGraph analyser={state.recorder.audioGraph.analyser} height={frequencyVisualizerHeight} />
          <ContainerOverFrequencyVisualizer>
            <PauseButton onClick={pauseRecording} size="lg" />
          </ContainerOverFrequencyVisualizer>
        </>
      )
    }

    case 'paused':
      return <RecordButtonAndFrequencyGraph startRecording={resumeRecording} />

    case 'countdown': {
      const recordingStartTimeMs = 'recordingStartTimeMs' in state ? state.recordingStartTimeMs : undefined

      return (
        <RecordButtonAndFrequencyGraph
          countdown={
            recordingStartTimeMs ? (
              <TickUpdated currentValue={(now) => Math.max(0, secondsUntil(recordingStartTimeMs, now))} />
            ) : (
              recordingCountdownSeconds
            )
          }
        />
      )
    }

    case 'uploading':
    case 'complete':
      return <></>
  }
}

function BottomControls({
  state,
  canDownload,
  dispatch,
  showDownloadButtonBeforeRecording,
  onEmergencyDownload,
}: {
  state: WebRecorder2State
  canDownload: boolean
  dispatch: Dispatch
  showDownloadButtonBeforeRecording: boolean
  onEmergencyDownload: () => void
}): React.ReactElement {
  switch (state.mode) {
    case 'initial':
      return <></>

    case 'initialized':
    case 'playing-disclosure':
    case 'countdown':
    case 'recording': {
      return (
        <div className="mt-2 flex flex-row gap-2 w-full items-center">
          <div className="truncate grow">
            <AudioInputSelector state={state.microphoneInputState} dispatch={dispatch} variant="labeled" />
          </div>
          {canDownload && showDownloadButtonBeforeRecording && state.mode !== 'recording' && hasRecording(state) && (
            <DownloadButton onClick={onEmergencyDownload} variant="just-icon" />
          )}
        </div>
      )
    }

    case 'paused':
    case 'uploading':
      return (
        <PausedBottomControls
          isOnline={state.isOnline}
          onSubmit={() => dispatch({ action: 'submit-recording' })}
          onDelete={() => dispatch({ action: 'delete-recording' })}
          onDownload={canDownload ? () => dispatch({ action: 'download-recording' }) : undefined}
          uploadPercent={'progressPercent' in state ? state.progressPercent : undefined}
        />
      )

    case 'complete':
      return (
        <div className="flex flex-row gap-2 items-center mt-2">
          <div className="grow text-secondary">Processing&hellip;</div>
          {canDownload && (
            <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} variant="just-icon" />
          )}
        </div>
      )
  }
}

function hasRecording(state: WebRecorder2State) {
  if ('recorder' in state && state.recorder) {
    return Recorder.recordingDurationSeconds(state.recorder)
  } else if ('storage' in state && state.storage) {
    return RecordingStorage.getDurationMs(state.storage) > 0
  } else {
    return false
  }
}

function PausedBottomControls({
  onSubmit,
  onDelete,
  onDownload,
  uploadPercent,
  isOnline,
}: {
  onSubmit?: () => void
  onDelete: () => void
  onDownload?: () => void
  uploadPercent?: number
  isOnline: boolean
}) {
  const submitted = uploadPercent !== undefined && uploadPercent >= 100

  return (
    <div className="flex flex-row gap-2 items-center mt-2">
      {submitted ? (
        <div className="flex flex-row items-center gap-x-2 text-primary grow">
          <CheckCircleIcon className="w-4 h-4 icon-success" /> Submitted
        </div>
      ) : typeof uploadPercent === 'number' ? (
        <div className="grow">
          <UploadProgress progressPercent={uploadPercent} tight />
        </div>
      ) : onSubmit ? (
        <Button
          onClick={onSubmit}
          disabled={!isOnline}
          tooltip={
            isOnline
              ? 'Upload the captured meeting now'
              : 'Your computer does not appear to be connected to the internet.'
          }
          variant="primary"
          size="sm"
          grow
        >
          Submit
        </Button>
      ) : (
        <div className="grow" />
      )}

      {typeof uploadPercent === 'undefined' && (
        <Modal
          title="Delete captured meeting?"
          trigger={
            <IconButton
              icon={<TrashIcon />}
              screenReaderText="Delete captured meeting"
              tooltip="Delete captured meeting"
              variant="just-icon"
            />
          }
          confirm={
            <button className="pc-button pc-button--danger pc-button--sm bg-danger-inverse py-2" onClick={onDelete}>
              Delete
            </button>
          }
        >
          <p className="max-w-[26rem]">Are you sure you want to delete this capture? This action cannot be undone.</p>
        </Modal>
      )}

      {onDownload && <DownloadButton onClick={onDownload} variant="just-icon" />}
    </div>
  )
}

function url(recording: UnsubmittedRecording) {
  return 'meetingId' in recording ? `/meetings/${recording.meetingId}` : `/prepare/${recording.intRefId}`
}

async function downloadDirectFromIndexedDb({
  subjectId,
  name,
  trackAnalytics,
}: {
  subjectId: string
  name: string
  trackAnalytics: Callbacks['trackAnalytics']
}) {
  try {
    const storage = await RecordingStorage.openExistingStorage(subjectId, 'notetaker')

    if (storage) {
      /* eslint-disable-next-line no-console */
      downloadRecording(storage, name, trackAnalytics, console.log)
    }
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.error('Error downloading recording', error)
    trackAnalytics(`web-recorder:download-local-recording-failed-inactive`)
  }
}
